import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Printer, ArrowLeft } from "lucide-react";

const money = (n) =>
    Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (n, d = 0) =>
    Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDate = (s) => {
    if (!s) return "";
    try {
        return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "Asia/Manila" });
    } catch {
        return s;
    }
};

// Gross = the marked-up / given price; Discounted = effective per-unit after ALL discounts
// (net_total / qty), matching what is posted to SAP.
const grossUnit = (l) => Number(l.given_price ?? l.markup_price ?? l.unit_price ?? 0);
const discountedUnit = (l) => {
    const q = Number(l.quantity || 0);
    const net = Number(l.net_total || 0);
    if (q > 0 && net > 0) return net / q;
    if (l.discounted_price != null && l.discounted_price !== "") return Number(l.discounted_price);
    return grossUnit(l) * (1 - Number(l.discount_pct || 0) / 100);
};

export default function SalesOrderPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    // The hidden-price group (price_visibility "none") sees no prices on screen, so the printout
    // must not carry them either (Sir Lynx 2026-10-29): no unit prices, line totals or totals.
    const showPrices = user?.price_visibility !== "none";
    const [so, setSo] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/sales-orders/${id}`);
                setSo(data);
            } catch (e) {
                setErr(formatApiError(e));
            }
        })();
    }, [id]);

    const totals = useMemo(() => {
        const lines = (so && so.lines) || [];
        const subtotal = so?.subtotal ?? lines.reduce((a, l) => a + Number(l.net_total || 0), 0);
        const other = so?.other_charges_total ?? (so?.other_charges || []).reduce((a, c) => a + Number(c.amount || 0), 0);
        const grand = so?.grand_total ?? subtotal + other;
        return { subtotal, other, grand };
    }, [so]);

    if (err) return <div style={{ padding: 24, color: "#b00" }}>{err}</div>;
    if (!so) return <div style={{ padding: 24 }}>Loading…</div>;

    const lines = so.lines || [];
    const otherCharges = so.other_charges || [];

    return (
        <div style={{ background: "#f3f4f6", minHeight: "100vh", padding: "16px 0" }}>
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: #fff !important; }
                    .so-sheet { box-shadow: none !important; margin: 0 !important; }
                }
                .so-sheet { width: 820px; margin: 0 auto; background: #fff; color: #111; padding: 40px 44px;
                    box-shadow: 0 1px 4px rgba(0,0,0,.15); font-family: Arial, Helvetica, sans-serif; font-size: 12px; }
                .so-sheet table { width: 100%; border-collapse: collapse; }
                .so-sheet th, .so-sheet td { border: 1px solid #333; padding: 5px 7px; }
                .so-sheet thead th { background: #eee; font-weight: 700; text-align: center; }
                .n { text-align: right; font-variant-numeric: tabular-nums; }
                .c { text-align: center; }
                .sigblock { display: flex; justify-content: space-between; gap: 24px; margin-top: 48px; }
                .sigblock > div { flex: 1; text-align: center; }
                .sigline { border-top: 1px solid #333; margin-top: 34px; padding-top: 4px; font-size: 11px; }
            `}</style>

            <div className="no-print" style={{ width: 820, margin: "0 auto 12px", display: "flex", gap: 8 }}>
                <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", border: "1px solid #ccc", borderRadius: 6, background: "#fff", cursor: "pointer" }}>
                    <ArrowLeft size={16} /> Back
                </button>
                <button onClick={() => window.print()} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", border: "none", borderRadius: 6, background: "#2563eb", color: "#fff", cursor: "pointer" }}>
                    <Printer size={16} /> Print
                </button>
            </div>

            <div className="so-sheet">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 1 }}>{so.company || "Sale Hub"}</div>
                        <div style={{ color: "#555" }}>Sales Order</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "monospace" }}>{so.doc_no}</div>
                        <div>Date: {fmtDate(so.created_at)}</div>
                        {so.source_quotation_no ? <div>SQ Ref: {so.source_quotation_no}</div> : null}
                    </div>
                </div>

                <table style={{ marginBottom: 16, border: "none" }}>
                    <tbody>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0", width: "50%" }}><b>Customer:</b> {so.end_customer_name || (so.customer_snapshot && so.customer_snapshot.company_name) || so.customer_name || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Customer Code:</b> {(so.customer_snapshot && so.customer_snapshot.customer_code) || so.customer_code || "—"}</td>
                        </tr>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Project:</b> {so.project_name || so.project || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Location:</b> {so.project_address || so.project_location || "—"}</td>
                        </tr>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0" }}><b>PO No.:</b> {so.po_no || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>SAP Order No.:</b> {so.sap_docnum || "—"}</td>
                        </tr>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Dealer / Store:</b> {so.customer_name || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Lead Source:</b> {so.lead_source || "—"}</td>
                        </tr>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Category:</b> {so.category || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Buyer:</b> {so.buyer || "—"}</td>
                        </tr>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Classification:</b> {so.classification || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Source Platform:</b> {so.source_platform || "—"}</td>
                        </tr>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0" }}><b>SPRF Beneficiary:</b> {so.beneficiary_name || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>BMF Approval:</b> {so.bmf_ref || "—"}</td>
                        </tr>
                        <tr>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Mode of Payment:</b> {so.payment_type || "—"}</td>
                            <td style={{ border: "none", padding: "2px 0" }}><b>Payment Verification:</b> {so.payment_verification || "—"}</td>
                        </tr>
                    </tbody>
                </table>

                <table>
                    <thead>
                        <tr>
                            <th className="c" style={{ width: 34 }}>#</th>
                            <th>Item</th>
                            <th className="c" style={{ width: 90 }}>Tonality</th>
                            <th className="c" style={{ width: 90 }}>Size</th>
                            <th className="n" style={{ width: 60 }}>Qty</th>
                            {showPrices && <th className="n" style={{ width: 90 }}>Gross Price</th>}
                            {showPrices && <th className="n" style={{ width: 90 }}>Disc Price</th>}
                            {showPrices && <th className="n" style={{ width: 100 }}>Line Total</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((l, i) => (
                            <tr key={l.id || l.line_id || i}>
                                <td className="c">{i + 1}</td>
                                <td>
                                    <div style={{ fontFamily: "monospace", fontSize: 11 }}>{l.item_code}</div>
                                    <div>{l.item_name || l.description || ""}</div>
                                </td>
                                <td className="c" style={{ fontFamily: "monospace", fontSize: 11 }}>{((l.allocation && l.allocation.allocated_batches) || []).map((b) => b.tonality || b.batch_code).filter(Boolean).join(", ") || l.declared_tonality || "—"}</td>
                                <td className="c">{l.size || "—"}</td>
                                <td className="n">{num(l.quantity, 0)}</td>
                                {showPrices && <td className="n">{money(grossUnit(l))}</td>}
                                {showPrices && <td className="n">{discountedUnit(l) < grossUnit(l) - 0.005 ? money(discountedUnit(l)) : "—"}</td>}
                                {showPrices && <td className="n">{money(l.net_total)}</td>}
                            </tr>
                        ))}
                        {showPrices && <tr>
                            <td colSpan={7} className="n" style={{ fontWeight: 600 }}>Subtotal</td>
                            <td className="n" style={{ fontWeight: 600 }}>{money(totals.subtotal)}</td>
                        </tr>}
                        {showPrices && otherCharges.map((c, i) => (
                            <tr key={`oc-${i}`}>
                                <td colSpan={7} className="n" style={{ color: "#555" }}>{c.label || c.type || "Other charge"}</td>
                                <td className="n">{money(c.amount)}</td>
                            </tr>
                        ))}
                        {showPrices && <tr>
                            <td colSpan={7} className="n" style={{ fontWeight: 800, background: "#eee" }}>GRAND TOTAL</td>
                            <td className="n" style={{ fontWeight: 800, background: "#eee" }}>{money(totals.grand)}</td>
                        </tr>}
                    </tbody>
                </table>

                {so.notes ? <div style={{ marginTop: 14 }}><b>Notes:</b> {so.notes}</div> : null}

                <div className="sigblock">
                    <div>
                        <div style={{ height: 34, display: "flex", alignItems: "flex-end", justifyContent: "center", fontWeight: 700 }}>{so.prepared_by_name || ""}</div>
                        <div className="sigline" style={{ marginTop: 0 }}>Prepared by</div>
                    </div>
                    <div><div className="sigline">Reviewed &amp; Noted</div></div>
                    <div><div className="sigline">Conforme</div></div>
                </div>
            </div>
        </div>
    );
}
